/* eslint-disable react-hooks/refs */
import { type JSX, type ReactNode, useId } from 'react';

import { FocusRing, Text } from '@change/ds-core-ui';
import { ErrorMessage } from '@change/ds-forms';
import { SearchIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

import { useComboBox } from './hooks/useComboBox';

type BareComboBoxProps<OPTION> = {
	id?: string;
	name: string;
	label: string;
	error?: ReactNode;
	inputValue?: string;
	onInputChange?: (event: React.ChangeEvent<HTMLInputElement> | null, newValue: string) => void;
	value?: OPTION | null;
	defaultValue?: OPTION | null;
	onChange?: (event: React.SyntheticEvent<HTMLElement> | null, option: OPTION | null) => void;
	options: readonly OPTION[];
	filterOptions?: (options: readonly OPTION[], inputValue: string) => readonly OPTION[];
	optionToString: (option: OPTION) => string;
	renderOption?: (option: OPTION) => JSX.Element;
	autoHighlight?: boolean;
};

type Props<OPTION> = BareComboBoxProps<OPTION>;

// eslint-disable-next-line max-lines-per-function
export function ComboBox<OPTION>({
	id: idProp,
	name,
	label,
	error,
	inputValue: controlledInputValue,
	onInputChange,
	value: controlledValue,
	defaultValue = null,
	onChange,
	options,
	filterOptions,
	optionToString,
	renderOption,
	autoHighlight = false,
}: Props<OPTION>): JSX.Element {
	const id = useOptionalId(idProp);
	const hasError = Boolean(error);

	const props = useComboBox<OPTION>({
		inputValue: controlledInputValue,
		onInputChange,
		value: controlledValue,
		defaultValue,
		onChange,
		options,
		filterOptions,
		optionToString,
		renderOption,
		autoHighlight,
	});

	return (
		<div className="inline-flex w-full flex-col gap-2">
			<label htmlFor={id}>
				<Text className="font-bold">{label}</Text>
			</label>

			{/* input + popover wrapper */}
			<div className="relative">
				{/* input wrapper */}
				<div className="relative">
					<SearchIcon className="text-icon-strong absolute top-1/2 left-3 h-6 -translate-y-1/2" />
					<FocusRing>
						<input
							id={id}
							name={name}
							type="text"
							ref={props.inputRef}
							role="combobox"
							autoComplete="off"
							aria-autocomplete="list"
							aria-invalid={hasError}
							aria-describedby={hasError ? `${id}-error` : undefined}
							aria-controls={`${id}-listbox`}
							aria-expanded={props.open}
							aria-activedescendant={props.activeIndex >= 0 ? `${id}-${props.activeIndex}` : undefined}
							value={props.inputValue}
							onChange={(e) => props.handleInputChange(e, e.currentTarget.value)}
							onKeyDown={props.handleInputKeyDown}
							onClick={props.handleInputClick}
							onBlur={props.handleInputBlur}
							className="border-stroke-strong bg-fill-inverse-strong text-typography-strong placeholder-typography-disabled focus-visible:outline-stroke-focus text-size-body-large h-14 w-full rounded-[16px] border border-solid py-4 pr-4 pl-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
							data-qa="edit-location-modal-input"
						/>
					</FocusRing>
				</div>
				{props.open && (
					<div className="rounded-small bg-background-base absolute top-[calc(100%+6px)] left-0 z-10 max-h-[190px] w-full overflow-y-auto border p-2 shadow-lg">
						{props.filteredOptions.length === 0 ? (
							<Text as="div" className="border-t border-b border-transparent px-3 py-3.5">
								<Translate value="corgi.components.combobox.no_options_label" />
							</Text>
						) : (
							<ul
								id={`${id}-listbox`}
								// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
								role="listbox"
								aria-labelledby={id}
								ref={props.listboxRef}
								className="font-body mx-0 list-none p-0"
							>
								{props.filteredOptions.map((option, index) => (
									// eslint-disable-next-line jsx-a11y/click-events-have-key-events
									<li
										key={props.optionToString(option)}
										id={`${id}-${index}`}
										// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
										role="option"
										aria-selected={props.activeIndex === index}
										data-highlighted={props.highlightedIndex === index}
										className="rounded-small aria-selected:bg-fill-hover data-[highlighted='true']:bg-fill-hover mx-0 flex cursor-pointer items-center justify-between border-t border-b border-transparent p-4"
										onClick={(e) => props.handleSelect(e, index)}
										onMouseEnter={() => props.handleMouseEnter(index)}
										onMouseDown={props.handleMouseDown}
										data-qa="edit-location-modal-option"
									>
										{renderOption ? renderOption(option) : optionToString(option)}
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>

			<div aria-live="polite">
				{hasError && (
					<div id={`${id}-error`} className="mt-2 flex space-x-4">
						<ErrorMessage>{error}</ErrorMessage>
					</div>
				)}
			</div>
		</div>
	);
}

function useOptionalId(id: string | undefined): string {
	const generatedId = useId();
	return id || generatedId;
}
